import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fmtDate, initials } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Shield, Users2, Loader2, Clock, UserPlus, KeyRound, Activity, Search, Ban, CheckCircle2, Eye, EyeOff, Plus, Building2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { setDrill } from "@/lib/drill";
import { setSAMode } from "@/lib/saMode";

const fmtDateTime = (v) => {
  if (!v) return "Never";
  const d = new Date(v);
  if (isNaN(d)) return "Never";
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Stat = ({ label, value, icon: Icon, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`admin-stat-${label}`}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone}`}><Icon size={20} /></div>
    <div>
      <div className="text-2xl font-heading font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-xs text-slate-400 uppercase tracking-wide mt-1">{label}</div>
    </div>
  </div>
);

export default function AdminOverview() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/admin-overview").then((r) => setData(r.data)).catch(() => nav("/dashboard"));
  useEffect(() => { load(); }, [nav]);

  const openSchool = (a) => {
    setDrill({ id: a.id, name: a.name });
    setSAMode("admin");
    toast.success(`Viewing ${a.name} — read-only`);
    nav("/dashboard");
  };

  const toggleStatus = async (a) => {
    const next = a.status === "Active" ? "Disabled" : "Active";
    if (next === "Disabled" && !window.confirm(`Disable ${a.username}? They will not be able to log in.`)) return;
    try {
      await api.put(`/users/${a.id}`, { status: next });
      toast.success(`${a.username} ${next === "Active" ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  // Reset password
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const doReset = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPw(true);
    try {
      await api.put(`/users/${resetTarget.id}`, { password: newPw });
      toast.success(`Password reset for ${resetTarget.username}`);
      setResetTarget(null); setNewPw("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  // Activity log
  const [activityFor, setActivityFor] = useState(null);
  const [rows, setRows] = useState(null);
  const [actionFilter, setActionFilter] = useState("");
  const [showPw, setShowPw] = useState({});

  // Add new school (admin)
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", password: "", phone: "" });
  const [creating, setCreating] = useState(false);
  const createSchool = async () => {
    if (!form.name.trim()) { toast.error("Enter the school name"); return; }
    if (!form.username.trim()) { toast.error("Enter a login username"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setCreating(true);
    try {
      await api.post("/users", { name: form.name.trim(), username: form.username.trim().toLowerCase(), password: form.password, phone: form.phone, role: "admin" });
      toast.success(`School "${form.name}" created`);
      setAddOpen(false); setForm({ name: "", username: "", password: "", phone: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const loadActivity = (acct, action = "") => {
    setRows(null);
    api.get("/admin-overview/activity", { params: { name: acct.name, action: action || undefined } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  const openActivity = (acct) => { setActivityFor(acct); setActionFilter(""); loadActivity(acct); };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><ShieldCheck className="text-indigo-600" />Admin Overview</h1>
          <p className="text-slate-500 text-sm mt-1">Super Admin control panel — admin accounts, who created whom, and last login.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-1.5" data-testid="add-school-btn"><Plus size={16} />Add New School</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat label="Super Admins" value={data.total_super_admins} icon={ShieldCheck} tone="bg-violet-100 text-violet-600" />
        <Stat label="Admins" value={data.total_admins} icon={Shield} tone="bg-indigo-100 text-indigo-600" />
        <Stat label="Total Users" value={data.total_users} icon={Users2} tone="bg-emerald-100 text-emerald-600" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll" data-testid="admin-overview-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Account</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created By</th>
              <th className="text-left px-4 py-3">Created On</th>
              <th className="text-left px-4 py-3"><span className="flex items-center gap-1"><Clock size={12} />Last Login</span></th>
              <th className="text-left px-4 py-3"><span className="flex items-center gap-1"><UserPlus size={12} />Accounts Created</span></th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.accounts.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50 align-top" data-testid={`admin-row-${a.username}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${a.role === "super_admin" ? "bg-violet-100 text-violet-600" : "bg-indigo-100 text-indigo-600"}`}>{initials(a.name || a.username)}</div>
                    <div><div className="font-medium text-slate-800">{a.name}</div>
                      <div className="text-xs text-slate-400 font-mono">{a.username}</div>
                      <div className="text-xs text-slate-500 font-mono flex items-center gap-1 mt-0.5" data-testid={`pw-${a.username}`}>
                        <KeyRound size={11} className="text-slate-400" />
                        <span>{a.password ? (showPw[a.id] ? a.password : "••••••") : "— (reset to set)"}</span>
                        {a.password && <button onClick={() => setShowPw((s) => ({ ...s, [a.id]: !s[a.id] }))} className="text-slate-400 hover:text-indigo-600" data-testid={`pw-toggle-${a.username}`}>{showPw[a.id] ? <EyeOff size={12} /> : <Eye size={12} />}</button>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${a.role === "super_admin" ? "bg-violet-50 text-violet-600" : "bg-indigo-50 text-indigo-600"}`}>{a.role.replace(/_/g, " ")}</span>
                </td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "Active" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>{a.status}</span></td>
                <td className="px-4 py-3 text-slate-600">{a.created_by}</td>
                <td className="px-4 py-3 text-slate-500">{a.created_at ? fmtDate(a.created_at) : "—"}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDateTime(a.last_login)}</td>
                <td className="px-4 py-3">
                  {a.created_count === 0 ? <span className="text-slate-300">—</span> : (
                    <div>
                      <span className="font-semibold text-slate-700">{a.created_count}</span>
                      <div className="text-xs text-slate-400 mt-0.5 max-w-xs">{a.created_users.map((u) => `${u.name} (${u.role})`).join(", ")}</div>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {a.role === "admin" && (
                    <Button size="sm" variant="ghost" onClick={() => openSchool(a)} className="gap-1 text-emerald-600" data-testid={`open-school-${a.username}`}><LogIn size={14} />Open</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openActivity(a)} className="gap-1" data-testid={`activity-${a.username}`}><Activity size={14} />Activity</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setResetTarget(a); setNewPw(""); }} className="gap-1 text-indigo-600" data-testid={`reset-pw-${a.username}`}><KeyRound size={14} />Reset</Button>
                  {a.username !== user?.username && (
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(a)} className={`gap-1 ${a.status === "Active" ? "text-rose-500" : "text-emerald-600"}`} data-testid={`toggle-status-${a.username}`}>{a.status === "Active" ? <><Ban size={14} />Disable</> : <><CheckCircle2 size={14} />Enable</>}</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPw(""); } }}>
        <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset Password{resetTarget ? ` — ${resetTarget.username}` : ""}</DialogTitle></DialogHeader>
          <div>
            <Label>New Password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 6 characters" className="mt-1" data-testid="reset-pw-input" autoFocus />
            <p className="text-xs text-slate-400 mt-2">The admin will use this new password on their next login.</p>
          </div>
          <DialogFooter><Button onClick={doReset} disabled={savingPw} data-testid="reset-pw-save">{savingPw ? <Loader2 className="animate-spin" size={16} /> : "Reset Password"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activityFor} onOpenChange={(o) => { if (!o) { setActivityFor(null); setRows(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="activity-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0"><DialogTitle>Activity — {activityFor?.name}</DialogTitle></DialogHeader>
          <div className="px-6 pb-3 shrink-0">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); loadActivity(activityFor, e.target.value); }} placeholder="Filter by action (e.g. deleted, fee, created)" className="pl-9" data-testid="activity-filter" />
            </div>
          </div>
          <div className="overflow-y-auto px-6 pb-6 flex-1">
            {rows === null ? (
              <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>
            ) : rows.length === 0 ? (
              <div className="py-10 text-center text-slate-400" data-testid="activity-empty">No activity found.</div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="activity-list">
                {rows.map((r) => (
                  <div key={r.id} className="py-2.5 flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-800">{r.action}</div>
                      <div className="text-xs text-slate-400">{r.module}{r.record_id ? ` · ${r.record_id}` : ""}</div>
                    </div>
                    <div className="text-xs text-slate-400 shrink-0">{r.timestamp ? new Date(r.timestamp).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-school-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 size={18} className="text-indigo-600" />Add New School</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>School / Admin Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Green Valley School" className="mt-1" data-testid="school-name-input" autoFocus /></div>
            <div><Label>Login Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="e.g. greenvalley" className="mt-1" data-testid="school-username-input" /></div>
            <div><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="mt-1" data-testid="school-password-input" /></div>
            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Contact number" className="mt-1" data-testid="school-phone-input" /></div>
            <p className="text-xs text-slate-400">Creates a fresh Admin account with no student or staff data. The admin manages their own school from here.</p>
          </div>
          <DialogFooter><Button onClick={createSchool} disabled={creating} data-testid="create-school-btn">{creating ? <Loader2 className="animate-spin" size={16} /> : "Create School"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
